import { initialesDuNom } from '../../composants/initiales.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { Button, DropdownMenu, IconButton, Kbd, type DropdownMenuEntry } from '@artosera/ui';
import { ChevronDown, LogOut, Menu, Plus, Search, Store, UserRound } from 'lucide-react';
import type { JSX } from 'react';
import { politiqueTheme } from '../../appearance/politique.js';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { CompteSession, GalerieSession } from '../../api/types.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useDefilement, useFairMode, useMenuMobile } from './hooks.js';
import { MenuAffichage } from './MenuAffichage.js';
import styles from './TopBar.module.css';

export interface TopBarProps {
  compte: CompteSession;
  galerie: GalerieSession;
  galeries: readonly GalerieSession[];
  galerieSlug: string;
  onOuvrirRecherche: () => void;
  onOuvrirMenuMobile: () => void;
  onDeconnexion: () => void;
  langue: 'fr' | 'en';
}

/** Barre supérieure Halo (3.2) : galerie, recherche ⌘K, nouvelle œuvre, compte. */
export function TopBar({
  compte,
  galerie,
  galeries,
  galerieSlug,
  onOuvrirRecherche,
  onOuvrirMenuMobile,
  onDeconnexion,
  langue: langueCompte,
}: TopBarProps): JSX.Element {
  const { t, i18n } = useTranslation(['commun', 'regie']);
  const langue = (i18n.resolvedLanguage ?? i18n.language ?? langueCompte).startsWith('en')
    ? 'en'
    : 'fr';
  const navigate = useNavigate();
  // Le mode foire est un module complémentaire (D21) : sans lui, ni bouton
  // du stand, ni bascule d'affichage foire.
  const foireOuverte = galerieAModule(galerie, 'mode-foire');
  const modeFoire = useFairMode() && foireOuverte;
  // Droits visibles (ARTO-53, § 4) : le comptable n'a pas `artworks:write`,
  // donc pas de « Nouvelle œuvre » (audit du 23 septembre 2026, § 1).
  const peutCreerOeuvre = galerieALaction(galerie, 'artworks:write');
  // Lot C : le thème se règle dans la console ; sans le choix laissé au
  // client, le menu du compte n'affiche aucun sélecteur de design.
  const designLibre = politiqueTheme(galerie).libre;
  // Responsive réel (ARTO-53, § 7) : sous 850 px, les libellés cèdent la
  // place — icône seule pour « Nouvelle œuvre », Mode foire sans texte —
  // plutôt qu'un en-tête qui déborde (audit du 23 septembre 2026, § 1).
  const etroit = useMenuMobile();
  const defile = useDefilement();
  const initiales = initialesDuNom(compte.nom);

  function itemGalerie(option: GalerieSession): DropdownMenuEntry {
    return {
      type: 'item',
      item: {
        key: option.id,
        label: option.nom ?? option.slug ?? option.id,
        onSelect: () => {
          if (option.slug) navigate(`/${option.slug}`);
        },
      },
    };
  }

  /**
   * Sélecteur de galerie regroupé par groupe (5.2, 3.2) : une entrée d'en-tête
   * par groupe qui compte au moins une galerie du compte, puis les galeries
   * qui n'appartiennent à aucun groupe. Sans aucun groupe (le cas courant),
   * la liste reste plate — pas d'en-tête pour un unique regroupement vide.
   */
  const entreesGalerie: DropdownMenuEntry[] = (() => {
    const groupes = compte.groupes ?? [];
    if (groupes.length === 0) return galeries.map(itemGalerie);

    const entrees: DropdownMenuEntry[] = [];
    const idsRegroupes = new Set<string>();

    for (const groupe of groupes) {
      const galeriesDuGroupe = galeries.filter((option) => groupe.galeries.includes(option.id));
      if (galeriesDuGroupe.length === 0) continue;
      if (entrees.length > 0) entrees.push({ type: 'separator' });
      entrees.push({ type: 'label', label: groupe.nom });
      for (const option of galeriesDuGroupe) {
        entrees.push(itemGalerie(option));
        idsRegroupes.add(option.id);
      }
    }

    const horsGroupe = galeries.filter((option) => !idsRegroupes.has(option.id));
    if (horsGroupe.length > 0) {
      if (entrees.length > 0) {
        entrees.push({ type: 'separator' });
        entrees.push({ type: 'label', label: t('regie:coquille.galerieHorsGroupe') });
      }
      for (const option of horsGroupe) entrees.push(itemGalerie(option));
    }

    return entrees;
  })();

  // En-tête compressible (ARTO-53, § 7 — retour architecte du 23 septembre
  // 2026 : l'icône du compte restait tronquée par le bord droit sous
  // 390 px malgré la réduction des libellés). Sous 850 px, « Nouvelle
  // œuvre » et le stand en cours rejoignent le menu du compte plutôt que de
  // rester des contrôles séparés qui ne tiennent pas tous ensemble : seuls
  // le menu, le nom de galerie, la recherche et le compte restent dans la
  // barre. Les réglages d'affichage (apparence, design, mode foire) vivent
  // dans ce même menu.
  const entreesCompte: DropdownMenuEntry[] = [
    { type: 'label', label: compte.nom },
    { type: 'separator' },
    ...(etroit && peutCreerOeuvre
      ? ([
          {
            type: 'item',
            item: {
              key: 'nouvelle-oeuvre',
              label: t('regie:coquille.actionNouvelleOeuvre'),
              icon: <Plus size={16} strokeWidth={1.5} aria-hidden />,
              onSelect: () => navigate(`/${galerieSlug}/oeuvres?nouvelle=1`),
            },
          },
        ] satisfies DropdownMenuEntry[])
      : []),
    ...(etroit && modeFoire
      ? ([
          {
            type: 'item',
            item: {
              key: 'foire-en-cours',
              label: t('regie:foire.foireEnCours'),
              icon: <Store size={16} strokeWidth={1.5} aria-hidden />,
              onSelect: () => navigate(`/${galerieSlug}/stand`),
            },
          },
        ] satisfies DropdownMenuEntry[])
      : []),
    ...(etroit ? ([{ type: 'separator' }] satisfies DropdownMenuEntry[]) : []),
    {
      type: 'item',
      item: {
        key: 'reglages',
        label: t('commun:navigation.reglages'),
        icon: <UserRound size={16} strokeWidth={1.5} aria-hidden />,
        onSelect: () => navigate(`/${galerieSlug}/reglages`),
      },
    },
    { type: 'separator' },
    {
      type: 'custom',
      key: 'affichage',
      content: (
        <MenuAffichage
          compteId={compte.id}
          langue={langue}
          designLibre={designLibre}
          foireOuverte={foireOuverte}
        />
      ),
    },
    { type: 'separator' },
    {
      type: 'item',
      item: {
        key: 'deconnexion',
        label: t('regie:coquille.deconnexion'),
        icon: <LogOut size={16} strokeWidth={1.5} aria-hidden />,
        onSelect: onDeconnexion,
      },
    },
  ];

  return (
    <header className={styles.barre} data-ui="topbar" data-defile={defile ? 'oui' : undefined}>
      <button
        type="button"
        className={styles.menuMobile}
        onClick={onOuvrirMenuMobile}
        aria-label={t('regie:coquille.menuMobileOuvrir')}
      >
        <Menu size={20} strokeWidth={1.5} aria-hidden />
      </button>

      {galeries.length > 1 ? (
        <DropdownMenu
          align="start"
          entries={entreesGalerie}
          trigger={
            <button type="button" className={styles.selecteurGalerie}>
              <span className={styles.nomGalerieTexte}>{galerie.nom ?? galerie.slug}</span>
              <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
            </button>
          }
        />
      ) : (
        <span className={styles.nomGalerie}>
          <span className={styles.nomGalerieTexte}>{galerie.nom ?? galerie.slug}</span>
        </span>
      )}

      <button
        type="button"
        className={styles.recherche}
        onClick={onOuvrirRecherche}
        aria-label={t('regie:coquille.rechercheChamp')}
      >
        <Search size={16} strokeWidth={1.5} aria-hidden />
        <span>{t('regie:coquille.rechercheChamp')}</span>
        <Kbd className={styles.raccourci}>⌘K</Kbd>
      </button>

      <div className={styles.actions}>
        {!etroit && peutCreerOeuvre ? (
          <Button
            variant="primary"
            data-ui="primary-action"
            className={styles.action}
            aria-label={t('regie:coquille.actionNouvelleOeuvre')}
            onClick={() => navigate(`/${galerieSlug}/oeuvres?nouvelle=1`)}
          >
            <Plus size={16} strokeWidth={1.5} aria-hidden />
            <span className={styles.libelleAction}>{t('regie:coquille.actionNouvelleOeuvre')}</span>
          </Button>
        ) : null}
        {!etroit && modeFoire ? (
          <Button
            variant="accent"
            className={styles.action}
            aria-label={t('regie:foire.foireEnCours')}
            onClick={() => navigate(`/${galerieSlug}/stand`)}
          >
            <Store size={16} strokeWidth={1.5} aria-hidden />
            <span className={styles.libelleAction}>{t('regie:foire.foireEnCours')}</span>
          </Button>
        ) : null}
        <DropdownMenu
          entries={entreesCompte}
          trigger={
            <IconButton
              className={styles.compte}
              icon={
                initiales ? (
                  <Monogramme nom={compte.nom} taille={32} className={styles.initiales} />
                ) : (
                  <UserRound aria-hidden />
                )
              }
              aria-label={t('regie:coquille.moncompte')}
            />
          }
        />
      </div>
    </header>
  );
}
